import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { LogOut, ImageOff, Heart, Download, CheckCircle2, ShieldCheck, CalendarDays, MapPin, CalendarPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { SEO } from '@/components/SEO';
import { useAuth } from '@/context/AuthContext';
import { getClientOwnedGallery, getMyFavorites, getMySelections, getMyBookings } from '@/lib/api';
import { GALLERY_FAVORITES, MY_BOOKINGS } from '@/constants/testIds';
import { SkeletonList } from '@/components/SkeletonList';

const BOOKING_STATUS_STYLE = {
  requested: { label: 'Pending confirmation', className: 'border-[hsl(var(--lxra-border))] bg-transparent text-[hsl(var(--lxra-muted))]' },
  booked: { label: 'Booked', className: 'border-[hsl(var(--lxra-gold))]/30 bg-[hsl(var(--lxra-gold))]/10 text-[hsl(var(--lxra-gold))]' },
  shot: { label: 'Shot — editing soon', className: 'border-sky-500/25 bg-sky-500/10 text-sky-300' },
  editing: { label: 'In editing', className: 'border-sky-500/25 bg-sky-500/10 text-sky-300' },
  delivered: { label: 'Delivered', className: 'border-emerald-500/25 bg-emerald-500/10 text-emerald-200' },
  cancelled: { label: 'Cancelled', className: 'border-red-500/25 bg-red-500/10 text-red-200' },
};

export default function ClientPortal() {
  const { user, logout } = useAuth();
  const [galleries, setGalleries] = useState(null);
  const [favorites, setFavorites] = useState({});
  const [selections, setSelections] = useState({});
  const [bookings, setBookings] = useState(null);

  useEffect(() => {
    getMyBookings().then(setBookings).catch(() => setBookings([]));
    if (!user?.gallery_slugs?.length) {
      setGalleries([]);
      return;
    }
    Promise.all(user.gallery_slugs.map((slug) => getClientOwnedGallery(slug).catch(() => null)))
      .then((results) => setGalleries(results.filter(Boolean)));
    getMyFavorites().then(setFavorites).catch(() => setFavorites({}));
    getMySelections().then(setSelections).catch(() => setSelections({}));
  }, [user]);

  return (
    <div className="min-h-[80vh] bg-[#0A0A0A] px-4 py-16" data-testid="client-portal-page">
      <SEO title="My Galleries" description="Your private LXRA Studios galleries." />
      <div className="mx-auto max-w-4xl">
        <div className="flex items-center justify-between mb-10">
          <div>
            <p className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--lxra-gold))]">Welcome back</p>
            <h1 className="font-display text-3xl tracking-[0.03em] text-[hsl(var(--lxra-fg))] mt-1">
              {user?.name || user?.email}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <Button
              asChild
              variant="outline"
              data-testid="client-account-security-link"
              className="border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))] hover:bg-[#141416] hover:text-[hsl(var(--lxra-gold))]"
            >
              <Link to="/client/security">
                <ShieldCheck className="h-4 w-4 mr-2" />
                Account Security
              </Link>
            </Button>
            <Button
              onClick={logout}
              variant="outline"
              data-testid="client-logout-button"
              className="border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))] hover:bg-[#141416] hover:text-[hsl(var(--lxra-gold))]"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Log Out
            </Button>
          </div>
        </div>

        <p className="mb-6 flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-[hsl(var(--lxra-muted))]">
          <span className="inline-flex items-center gap-1.5"><Heart className="h-3.5 w-3.5 text-[hsl(var(--lxra-gold))]" /> Heart the photos you love — your photographer sees your picks.</span>
          <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5 text-[hsl(var(--lxra-gold))]" /> Mark your final picks and leave notes for revisions.</span>
          <span className="inline-flex items-center gap-1.5"><Download className="h-3.5 w-3.5 text-[hsl(var(--lxra-gold))]" /> Download any photo from the viewer.</span>
        </p>

        {/* My Bookings */}
        <div className="mb-12" data-testid={MY_BOOKINGS.root}>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-xl tracking-[0.03em] text-[hsl(var(--lxra-fg))]">My Bookings</h2>
            <Button
              asChild
              size="sm"
              variant="outline"
              data-testid={MY_BOOKINGS.bookAnotherLink}
              className="border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))] hover:bg-[#141416] hover:text-[hsl(var(--lxra-gold))]"
            >
              <Link to="/book"><CalendarPlus className="h-3.5 w-3.5 mr-1.5" /> Book a Session</Link>
            </Button>
          </div>

          {bookings === null && <Skeleton className="h-24 rounded-2xl bg-[#141416]" />}

          {bookings?.length === 0 && (
            <div data-testid={MY_BOOKINGS.empty} className="rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] p-8 text-center">
              <CalendarDays className="h-7 w-7 text-[hsl(var(--lxra-muted))] mx-auto mb-3" />
              <p className="text-sm text-[hsl(var(--lxra-muted))]">No sessions booked yet.</p>
              <Button
                asChild
                size="sm"
                className="mt-4 bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] hover:bg-[hsl(var(--lxra-gold-hover))]"
              >
                <Link to="/book">Book your first session</Link>
              </Button>
            </div>
          )}

          {bookings?.length > 0 && (
            <div className="space-y-3">
              {bookings.map((b) => {
                const st = BOOKING_STATUS_STYLE[b.status] || BOOKING_STATUS_STYLE.booked;
                return (
                  <div
                    key={b.id}
                    data-testid={MY_BOOKINGS.row(b.id)}
                    className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] px-5 py-4"
                  >
                    <div>
                      <p className="text-sm font-medium text-[hsl(var(--lxra-fg))]">
                        {b.package_name || 'Session'} with {b.studio_name}
                      </p>
                      <p className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[hsl(var(--lxra-muted))]">
                        <span className="inline-flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" /> {b.session_date}{b.session_time ? ` · ${b.session_time}` : ''}</span>
                        {b.location && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {b.location}</span>}
                      </p>
                    </div>
                    <Badge data-testid={MY_BOOKINGS.status(b.id)} variant="outline" className={`uppercase text-[10px] tracking-[0.08em] ${st.className}`}>
                      {st.label}
                    </Badge>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {!galleries && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <SkeletonList count={2} className="h-56 rounded-2xl bg-[#141416]" />
          </div>
        )}

        {galleries?.length === 0 && (
          <div className="rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] p-10 text-center">
            <ImageOff className="h-8 w-8 text-[hsl(var(--lxra-muted))] mx-auto mb-3" />
            <p className="text-sm text-[hsl(var(--lxra-muted))]">
              No galleries have been linked to your account yet. Reach out to the studio if you're expecting one.
            </p>
          </div>
        )}

        {galleries?.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="client-portal-gallery-grid">
            {galleries.map((g) => {
              const favCount = favorites[g.slug]?.length || 0;
              const selCount = selections[g.slug]?.length || 0;
              return (
                <Link
                  key={g.slug}
                  to={`/galleries/${g.slug}`}
                  data-testid={`client-portal-gallery-card-${g.slug}`}
                  className="group relative block aspect-[4/3] overflow-hidden rounded-2xl border border-[hsl(var(--lxra-border))]"
                >
                  <img
                    src={g.cover_image_url}
                    alt={g.name}
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                  <div className="absolute top-4 right-4 flex items-center gap-2">
                    <span className="rounded-full bg-black/60 backdrop-blur border border-white/15 px-2.5 py-1 text-[11px] text-white">
                      {g.images?.length || 0} photos
                    </span>
                    {selCount > 0 && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-[hsl(var(--lxra-gold))] border border-[hsl(var(--lxra-gold))] text-[#0A0A0A] backdrop-blur px-2.5 py-1 text-[11px]">
                        <CheckCircle2 className="h-3 w-3 fill-current" /> {selCount}
                      </span>
                    )}
                    <span
                      className={`inline-flex items-center gap-1 rounded-full backdrop-blur border px-2.5 py-1 text-[11px] ${
                        favCount ? 'bg-[hsl(var(--lxra-gold))] border-[hsl(var(--lxra-gold))] text-[#0A0A0A]' : 'bg-black/60 border-white/15 text-white'
                      }`}
                      data-testid={GALLERY_FAVORITES.portalCount(g.slug)}
                    >
                      <Heart className={`h-3 w-3 ${favCount ? 'fill-current' : ''}`} /> {favCount}
                    </span>
                  </div>
                  <div className="absolute bottom-0 left-0 p-5">
                    <h3 className="font-display text-xl text-white tracking-[0.03em]">{g.name}</h3>
                    <p className="text-xs text-white/70 mt-1">{g.description}</p>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
